"use client";

import Script from "next/script";
import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import { initGA, trackPageView } from "@/utils/analytics";
import { getRuntimeEnvironment } from "@/utils/helpers/runtimeEnvironment";

const runtimeEnvironment = getRuntimeEnvironment();
const isProduction = runtimeEnvironment.isProduction;

// ---------------------------------------------------------------------------
// TEMP BYPASS (staging PostHog validation):
// The analytics layer was gated to production-only. We are bypassing that guard
// so we can verify the PostHog integration on staging before pushing it to
// production. GA / GTM / Clarity / AdSense intentionally remain production-only
// and are NOT affected by this flag.
//
// TO RESTORE (after staging validation):
//   1. Set BYPASS_POSTHOG_PROD_GUARD back to `false`.
//   2. Reinstate the `if (!isProduction) return null;` early return in Analytics.
//   3. Remove the `isProduction &&` wrapper around the third-party scripts and
//      restore RouteTracker's `if (!isProduction) return;` guard.
// ---------------------------------------------------------------------------
const BYPASS_POSTHOG_PROD_GUARD = true;
const shouldTrack = isProduction || BYPASS_POSTHOG_PROD_GUARD;

const GA_MEASUREMENT_ID = "G-7VZ0V2ETB7";
const GTM_ID = process.env.NEXT_PUBLIC_GTM_ID;
const ADSENSE_PUBLISHER_ID = process.env.NEXT_PUBLIC_ADSENSE_PUBLISHER_ID;
const ADSENSE_ENABLED = process.env.NEXT_PUBLIC_ADSENSE_ENABLED === "true";
const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;

function RouteTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isFirstRender = useRef(true);

  useEffect(() => {

    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const url =
      pathname +
      (searchParams?.toString() ? `?${searchParams.toString()}` : "");
    trackPageView({
      page_title: document.title,
      page_path: url,
      page_location: window.location.href,
    });
    posthog.capture("$pageview", {
      $current_url: window.location.href,
      page_path: url,
    });
  }, [pathname, searchParams]);

  return null;
}

export function Analytics() {
  useEffect(() => {
    if (isProduction) {
      console.log(
        `[Analytics] Runtime Environment: Production (${isProduction}). Initializing GA and tracking services...`,
      );
      if (typeof window !== "undefined") {
        initGA(GA_MEASUREMENT_ID);
      }
    } else {
      console.log(
        `[Analytics] Runtime Environment: Non-Production (${isProduction}). Tracking scripts disabled.`,
      );
    }
  }, []);

  // TEMP BYPASS: intentionally NOT returning null on non-production while the
  // PostHog staging bypass is active, otherwise <RouteTracker /> below would
  // never mount and no PostHog pageviews would be captured.
  // GA / GTM / Clarity / AdSense stay wrapped in `isProduction` so staging only
  // picks up PostHog during the validation window.
  // TO RESTORE: reinstate `if (!isProduction) return null;` and remove the
  // `isProduction &&` wrapper around the third-party scripts below.
  return (
    <>
      {isProduction && (
        <>
          {/* Google Analytics Script */}
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
          />

          <Script
            id="google-analytics-config"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', '${GA_MEASUREMENT_ID}', {
                  page_path: window.location.pathname,
                  debug_mode: false,
                  anonymize_ip: true,
                  cookie_flags: 'SameSite=None;Secure'
                });
              `,
            }}
          />

          {/* Google Tag Manager */}
          {GTM_ID && (
            <>
              <Script
                id="google-tag-manager"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                  __html: `
                    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                    })(window,document,'script','dataLayer','${GTM_ID}');
                  `,
                }}
              />
              <noscript>
                <iframe
                  src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
                  height="0"
                  width="0"
                  style={{ display: "none", visibility: "hidden" }}
                  title="Google Tag Manager"
                />
              </noscript>
            </>
          )}

          {/* Microsoft Clarity */}
          {CLARITY_PROJECT_ID && (
            <Script
              id="microsoft-clarity"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{
                __html: `
                  (function(c,l,a,r,i,t,y){
                    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
                  })(window, document, "clarity", "script", "${CLARITY_PROJECT_ID}");
                `,
              }}
            />
          )}

          {/* Google AdSense */}
          {ADSENSE_ENABLED && ADSENSE_PUBLISHER_ID && (
            <Script
              id="google-adsense"
              src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_PUBLISHER_ID}`}
              strategy="afterInteractive"
              crossOrigin="anonymous"
              onError={(e) => {
                console.error("AdSense script failed to load:", e);
              }}
            />
          )}
        </>
      )}

      {/* RouteTracker is always mounted so PostHog pageviews work under the
          temporary staging bypass as well as in production. */}
      <Suspense fallback={null}>
        <RouteTracker />
      </Suspense>
    </>
  );
}
